import React, { useRef, useState } from 'react';
import { useExpanded, usePagination, useRowSelect, useSortBy, useTable } from 'react-table';
import { Spinner } from 'react-bootstrap';
import { isValueEmpty } from '../helpers/utilsReact';
import PropTypes from 'prop-types';
import AwpSpinner from '@components/Common/AwpSpinner';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { IconCaretDownFill } from '@components/awpIcons';
import Pagination from '@components/Common/Pagination';

export const MAX_PAGE_SIZE = 999999999;
export const DEFAULT_PAGE_SIZE = 25;

let overlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

let spinnerStyle = {
  position: 'fixed',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 1001,
};

const SelectedRowsSelectAction = ({ options, handleChange, selectedRows, updateloading }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const onCancelChangeClick = () => {
    setSelectedStatus('');
    setShowConfirm(false);
  };

  const onStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setShowConfirm(e.target.value != '');
  };

  return (
    <>
      <select name="updateMultipleRows" onChange={onStatusChange} value={selectedStatus}>
        <option value="">Update status</option>
        {options.map((opt) => {
          return (
            <option key={opt.value} value={opt.value}>
              {opt.text}
            </option>
          );
        })}
      </select>

      {showConfirm ? (
        <>
          <span className="ml-3">
            Are you sure you want to update status?
            <button type="button" className="btn btn-outline-secondary ml-3" onClick={onCancelChangeClick}>
              No
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary ml-3"
              onClick={() => handleChange(selectedStatus, selectedRows)}
            >
              Yes
            </button>
          </span>
          {updateloading && <AwpSpinner style={{ marginLeft: '0.4rem' }} />}
        </>
      ) : (
        ''
      )}
    </>
  );
};

// @CODESMELL validator set to any
SelectedRowsSelectAction.propTypes = {
  options: PropTypes.any,
  handleChange: PropTypes.any,
  selectedRows: PropTypes.any,
};

export const cellComponentExp = ({
  row,
  rows,
  toggleRowExpanded,
  isChild,
  setExpandedRowEntityId,
  setScrollPosition,
  EntityId,
}) => {
  if (!row || !row.getToggleRowExpandedProps) return '';

  return (
    // Use Cell to render an expander for each row.
    // We can use the getToggleRowExpandedProps prop-getter
    // to build the expander.
    <span
      {...row.getToggleRowExpandedProps({
        onClick: () => {
          const expandedRow = rows.find((row) => row.isExpanded);

          if (expandedRow) {
            const isSubItemOfRow = Boolean(expandedRow && row.id.split('.')[0] === expandedRow.id);

            if (isSubItemOfRow) {
              const expandedSubItem = expandedRow.subRows.find((subRow) => subRow.isExpanded);

              if (expandedSubItem) {
                const isClickedOnExpandedSubItem = expandedSubItem.id === row.id;
                if (!isClickedOnExpandedSubItem) {
                  toggleRowExpanded(expandedSubItem.id, false);
                }
              }
            } else {
              toggleRowExpanded(expandedRow.id, false);
            }
          }

          row.toggleRowExpanded();

          // Handle expanded row state
          if (row.isExpanded) {
            setExpandedRowEntityId(null); // If row is already open, clear
          } else {
            setScrollPosition(window.scrollY); // Expanded row will change the scroll position
            setExpandedRowEntityId(row.original[EntityId]);
          }
        },
      })}
    >
      {row.isExpanded ? isChild ? <IconCaretDownFill /> : '' : <i className="fas fa-outdent mx-1 my-1 awp-img-btn" />}
    </span>
  );
};

const SelectedRowsButtonAction = ({ buttonText, handleClick, selectedRows }) => {
  const [showConfirm, setShowConfirm] = useState(false);

  const onButtonClick = () => {
    setShowConfirm(true);
  };

  const onCancelChangeClick = () => {
    setShowConfirm(false);
  };

  return (
    <>
      <button type="button" className="btn btn-outline-primary" onClick={onButtonClick}>
        {' '}
        {buttonText}{' '}
      </button>
      {showConfirm ? (
        <span className="ml-3">
          Are you sure you want to associate equipment?
          <button type="button" className="btn btn-outline-secondary ml-3" onClick={onCancelChangeClick}>
            No
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary ml-3"
            onClick={(e) => handleClick(e, selectedRows)}
          >
            Yes
          </button>
        </span>
      ) : (
        ''
      )}
    </>
  );
};

// @CODESMELL validator set to any
SelectedRowsButtonAction.propTypes = {
  buttonText: PropTypes.any,
  handleClick: PropTypes.any,
  selectedRows: PropTypes.any,
};

const IndeterminateCheckbox = React.forwardRef(({ indeterminate, notSelectable, ...rest }, ref) => {
  const defaultRef = React.useRef();
  const resolvedRef = ref || defaultRef;

  React.useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return (
    <>
      {notSelectable ? (
        <>
          <input type="checkbox" ref={resolvedRef} {...rest} disabled />
        </>
      ) : (
        <>
          <input type="checkbox" ref={resolvedRef} {...rest} />
        </>
      )}
    </>
  );
});
//resolves linter error Component definition is missing display name
IndeterminateCheckbox.displayName = 'IndeterminateCheckbox';

//https://stackoverflow.com/questions/67857563/react-table-conditionally-select-row
const getConditionalSelectHeaderCheckboxProps = ({ headerProps, isRowSelectable, shouldSelectPage = true }) => {
  const checkIfAllSelectableRowsSelected = (rows) => rows.filter(isRowSelectable).every((row) => row.isSelected);
  // Standard: Here we define the selection type for the next click: Select Page / Select All
  const isSelectPage =
    shouldSelectPage &&
    headerProps.page
      // For conditional select: Filter the rows based on your business logic
      .filter(isRowSelectable)
      // Standard: `isSelectPage === true` if some of the rows are not yet selected
      // This (standard) logic might be confusing to understand at first, but - as a side note - the idea is as follows:
      // This is the variable that defines whether the header props that will be received FOR THE NEXT CLICK will be for Select Page or for Select All
      // Try to play this out in your head:
      //  - Initially, none of the rows are selected, so when we clicking the button initially, we will select only the (selectable) rows on the page (i.e. Select Page), hence the next click will be for Select All, hence `isSelectPage` will be `false`
      //  - When clicking again, we will select the rest of the (selectable) rows (i.e. Select All). The next click will again be Select All (for de-selecting all), hence `isSelectPage` will be `false`
      //  - Finally, when clicking again, we will de-select all rows. The next click will be for Select Page, hence `isSelectPage` will `true`
      .some((row) => !row.isSelected);

  // Standard: Get the props based on Select Page / Select All
  const checkboxProps = isSelectPage
    ? headerProps.getToggleAllPageRowsSelectedProps()
    : headerProps.getToggleAllRowsSelectedProps();

  // For conditional select: The header checkbox should be:
  //   - checked if all selectable rows are selected
  //   - indeterminate if only some selectable rows are selected (but not all)
  const disabled = headerProps.rows.filter(isRowSelectable).length === 0;
  const checked = !disabled && checkIfAllSelectableRowsSelected(headerProps.rows);
  const indeterminate = !checked && headerProps.rows.some((row) => row.isSelected);

  // For conditional select: This is where the magic happens
  const onChange = () => {
    // If we're in Select All and all selectable rows are already selected: deselect all rows
    if (!isSelectPage && checkIfAllSelectableRowsSelected(headerProps.rows)) {
      headerProps.rows.forEach((row) => {
        headerProps.toggleRowSelected(row.id, false);
      });
    } else {
      // Otherwise:
      // First, define the rows to work with: if we're in Select Page, use `headerProps.page`, otherwise (Select All) use headerProps.rows
      const rows = isSelectPage ? headerProps.page : headerProps.rows;
      // Then select every selectable row
      rows.forEach((row) => {
        const checked = isRowSelectable(row);
        headerProps.toggleRowSelected(row.id, checked);
      });
    }
  };

  // For conditional select: override checked, indeterminate and onChange - to enforce conditional select based on our business logic
  return {
    ...checkboxProps,
    checked,
    indeterminate,
    onChange,
    disabled,
  };
};

// if index and setIndex are setup in Table, the parent component where Table is referred must pass defaultPageSize & setDefaultPageSize
const getPageIndex = (defaultPageSize, index, data, setIndex) => {
  const pageSize = isValueEmpty(defaultPageSize) ? DEFAULT_PAGE_SIZE : defaultPageSize;
  const pageIndex = index ?? 0;
  if (!index) return 0;
  const firstRowOnThePage = pageIndex * pageSize + 1;

  if (data.length < firstRowOnThePage) {
    if (setIndex) setIndex(0);
    return 0;
  } else {
    return index;
  }
};

function Table({
  columns,
  data,
  fetchData,
  loading,
  search,
  filters,
  noRecordsFound,
  defaultSortBy,
  defaultDesc,
  selectableRows,
  dataTriggers,
  hiddenColumns,
  isFirstColumnSticky,
  renderRowSubComponent,
  trRowStyle,
  expendRowStyle,
  expendCellStyle,
  treeExpand, //true/false-- if true, the columns of expanded child will align with parent columns
  doubleClickHandler,
  mouseDownHandler = () => {},
  mouseUpHandler = () => {},
  mouseMoveHandler = () => {},
  mouseEnterHandler = () => {},
  scrollHandler = () => {},
  onClickHandler,
  onCellStyle = () => ({}),
  defaultPageSize, // must be one of [5, 10, 25, 50, 100]
  setDefaultPageSize,
  index, //when index and setIndex are used in Table, defaultPageSize and setDefaultPageSize are also required
  setIndex, //when index and setIndex are used in Table, defaultPageSize and setDefaultPageSize are also required
  sort,
  setSort,
  tdRowStyle,
  thRowStyle,
  tableParentClasses = null, //default to null so it doesn't break current functionality
  tableClasses = '',
  paginationBarClasses = null, //default to null so it doesn't break current functionality
  showPagination = true,
  simpleBar = true,
  maxHeight = 500, //simpleBar maxHeight
  customClass = '', //simpleBar custom class
  maxPageSize = null, // min must be 200. it will replace All option
  scrollPosition = null, //used for optionally controlling the scroll position of the table within it's container
  setScrollPosition = null, //used for optionally controlling the scroll position of the table within it's container
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    visibleColumns,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, selectedRowIds, pageList, sortBy },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: getPageIndex(defaultPageSize, index, data, setIndex),
        pageSize: isValueEmpty(defaultPageSize) ? DEFAULT_PAGE_SIZE : defaultPageSize,
        pageList: isValueEmpty(defaultPageSize)
          ? [10, 25, 30, 50, 100, isValueEmpty(maxPageSize) ? 'All' : maxPageSize]
          : defaultPageSize == 5
          ? [5, 10, 25, 30, 50, 100, isValueEmpty(maxPageSize) ? 'All' : maxPageSize]
          : [10, 25, 30, 50, 100, isValueEmpty(maxPageSize) ? 'All' : maxPageSize],
        sortBy: sort ?? [
          {
            id: isValueEmpty(defaultSortBy) ? 'Name' : defaultSortBy,
            desc: isValueEmpty(defaultDesc) || defaultDesc === false ? false : true,
          },
        ],
        search: search,
        hiddenColumns: hiddenColumns ? hiddenColumns : [''],
        isFirstColumnSticky: isValueEmpty(isFirstColumnSticky) ? false : isFirstColumnSticky,
        expendRowStyle: isValueEmpty(expendRowStyle) ? '' : expendRowStyle,
        expendCellStyle: isValueEmpty(expendCellStyle) ? '' : expendCellStyle,
      },
      disableSortRemove: true,
      autoResetExpanded: false,
    },
    useSortBy,
    useExpanded, // Use the useExpanded plugin hook
    usePagination,
    useRowSelect,
    (hooks) => {
      if (selectableRows && selectableRows.enabled) {
        hooks.visibleColumns.push((columns) => [
          // Let's make a column for selection
          {
            id: 'selection',
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: (props) => {
              const checkboxProps = getConditionalSelectHeaderCheckboxProps({
                headerProps: props,
                isRowSelectable: (row) => !row.original.NotSelectable,
              });

              return <IndeterminateCheckbox {...checkboxProps} />;
            },
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }) => {
              return (
                <IndeterminateCheckbox
                  {...row.getToggleRowSelectedProps()}
                  notSelectable={row.original.NotSelectable}
                />
              );
            },
            style: { width: '20px', paddingLeft: '0.75rem' },
          },
          ...columns,
        ]);
      }
    },
  );

  const barRef = useRef(null);
  const tableContainerRef = useRef(null);

  const [pageLoading, setPageLoading] = useState(false);

  if (!filters) {
    filters = [];
  }
  if (!dataTriggers) {
    dataTriggers = [];
  }

  const getColumnStyle = (c) => {
    return c.style;
  };

  React.useEffect(() => {
    fetchData();
  }, [search, ...filters, ...dataTriggers]);

  React.useEffect(() => {
    if (!setIndex) return;

    if (index != pageIndex) {
      setIndex(pageIndex);
    }
    setPageLoading(false);
  }, [pageIndex, index, loading]);

  React.useEffect(() => {
    if (!setSort) return;

    if (sort != sortBy) {
      setSort(sortBy);
    }
  }, [sortBy, sort]);

  React.useEffect(() => {
    if (!setDefaultPageSize) return;

    setDefaultPageSize(pageSize);
  }, [pageSize, loading]);

  /**
   * useEffect hook to dynamically attach a scroll event listener to either the
   * table container or the window depending on the `maxHeight` value.
   *
   * - If `maxHeight` is 0, the scroll event will be attached to the window to track page-level scroll.
   * - Otherwise, the scroll event will be attached to the `tableContainerRef` to track table scroll.
   *
   * @param {number} maxHeight - The maximum height of the table container. If 0, the window is scrolled.
   * @param {object} tableContainerRef - Ref to the table container that may have its own scroll event.
   * @param {function} handleScroll - Function that handles scroll events and sets scroll position.
   */
  React.useEffect(() => {
    // Determine whether to listen to window scroll or table container scroll based on maxHeight
    const targetElement = maxHeight === 0 ? window : tableContainerRef.current;

    // Attach scroll event listener to the appropriate element
    if (targetElement) {
      targetElement.addEventListener('scroll', handleScroll);
    }

    // Cleanup function to remove the scroll event listener when component unmounts or dependencies change
    return () => {
      if (targetElement) {
        targetElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [maxHeight, tableContainerRef]);

  /**
   * Restore scroll position on mount
   * @param {number} scrollPosition - The saved scroll position to restore.
   * @param {object} barRef - Ref to the SimpleBar component.
   * @param {object} tableContainerRef - Ref to the table container.
   */
  React.useEffect(() => {
    const scrollToPosition = () => {
      if (scrollPosition > 0 && !loading) {
        if (barRef.current) {
          // Restore scroll position for SimpleBar component
          const scrollElement = barRef.current.getScrollElement();
          scrollElement.scrollTop = scrollPosition;
        } else if (tableContainerRef.current && maxHeight !== 0) {
          // Restore scroll position for table container
          tableContainerRef.current.scrollTop = scrollPosition;
        } else {
          // Restore scroll position for entire page
          window.scrollTo(0, scrollPosition);
        }
      }
    };

    requestAnimationFrame(scrollToPosition);
  }, [scrollPosition, loading, maxHeight]);

  /**
   * Handle scroll event
   * @param {Event} e - The scroll event triggered by the user.
   * @param {function} setScrollPosition - Function to set the current scroll position.
   * @param {function} [scrollHandler] - Optional function to handle custom scroll behavior.
   */
  const handleScroll = (e) => {
    if (setScrollPosition) {
      // If maxHeight is 0, track page scroll
      if (maxHeight === 0) {
        setScrollPosition(window.scrollY);
      } else {
        // Otherwise track the element's scroll position
        setScrollPosition(e.target.scrollTop);
      }
    }

    // Optionally, call a custom scroll handler if provided
    if (typeof scrollHandler === 'function') {
      scrollHandler(e);
    }
  };

  const renderTableContent = () => {
    return (
      <>
        {(pageLoading || loading) && (
          <>
            <div style={overlayStyle}></div>
            <div>
              <div className="text-center mt-4" style={spinnerStyle}>
                <p>Loading Data...</p>
                <Spinner animation="border" />
              </div>
            </div>
          </>
        )}
        <table {...getTableProps()} className={`table ${tableClasses}`}>
          <thead>
            {headerGroups.map((headerGroup, hdrIdx) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={'hdr-' + hdrIdx}>
                {headerGroup.headers.map((column, hIdx) => {
                  if (column.show || column.show === undefined) {
                    return (
                      <th
                        key={'h-' + hIdx}
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        style={{ ...column.getHeaderProps().style, ...getColumnStyle(column), ...thRowStyle }}
                      >
                        {column.render('Header')}
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <div className="arrow-down"></div>
                          ) : (
                            <div className="arrow-up"></div>
                          )
                        ) : (
                          ''
                        )}
                      </th>
                    );
                  }
                })}
              </tr>
            ))}
          </thead>
          <>
            {!pageLoading && !loading && rows.length < 1 ? (
              <tbody>
                <tr>
                  <td className="text-center" colSpan={visibleColumns.length}>
                    <span>{isValueEmpty(noRecordsFound) ? 'No records found' : noRecordsFound}</span>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody {...getTableBodyProps()}>
                {page.map((row, i) => {
                  prepareRow(row);
                  const rowProps = row.getRowProps();
                  return (
                    <React.Fragment key={rowProps.key}>
                      <tr {...rowProps} style={trRowStyle}>
                        {row.cells.map((cell, j) => {
                          if (cell.column.show || cell.column.show === undefined) {
                            if (isFirstColumnSticky === true && j === 0) {
                              return (
                                <th
                                  key={j}
                                  {...cell.getCellProps()}
                                  style={{
                                    ...cell.column.getHeaderProps().style,
                                    ...getColumnStyle(cell.column),
                                    ...thRowStyle,
                                  }}
                                >
                                  {cell.render('Cell')}
                                </th>
                              );
                            }

                            return (
                              <td
                                id={`${i}-${j}`}
                                key={j}
                                {...cell.getCellProps()}
                                style={{
                                  ...cell.column.getHeaderProps().style,
                                  ...getColumnStyle(cell.column),
                                  ...tdRowStyle,
                                  ...onCellStyle(cell, row.cells, i, j),
                                }}
                                onClick={(e) => {
                                  if (onClickHandler) {
                                    onClickHandler(e, cell);
                                  }
                                }}
                                onDoubleClick={(e) => {
                                  if (doubleClickHandler) {
                                    doubleClickHandler(e, cell);
                                    doubleClickHandler(e, cell);
                                  }
                                }}
                                onMouseDown={(e) => mouseDownHandler(e, page, cell, row.cells, i, j)}
                                onMouseUp={(e) => mouseUpHandler(e, cell, row.cells, i, j)}
                                onMouseMove={(e) => mouseMoveHandler(e, cell, row.cells, i, j)}
                                onMouseEnter={(e) => mouseEnterHandler(e, cell, row.cells, i, j)}
                                onTouchStart={(e) => mouseDownHandler(e, page, cell, row.cells, i, j)}
                                onTouchEnd={(e) => mouseUpHandler(e, cell, row.cells, i, j)}
                                onTouchMove={(e) => mouseMoveHandler(e, cell, row.cells, i, j)}
                              >
                                {cell.render('Cell')}
                              </td>
                            );
                          }
                        })}
                      </tr>
                      {!treeExpand && row.isExpanded ? (
                        <tr style={expendRowStyle}>
                          <td colSpan={visibleColumns.length} style={expendCellStyle}>
                            {renderRowSubComponent({ row, rows })}
                          </td>
                        </tr>
                      ) : null}
                      {treeExpand && row.isExpanded ? <>{renderRowSubComponent({ row, rows })}</> : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            )}
          </>
        </table>
      </>
    );
  };

  const nonSimpleBarTableStyle = {
    ...(maxHeight !== 0 && { maxHeight }),
    overflowY: 'auto',
    position: 'relative',
  };

  return (
    <>
      {Object.keys(selectedRowIds).length > 0 ? (
        <div className="mb-3">
          <span className="pr-2">
            {Object.keys(selectedRowIds).length} record{Object.keys(selectedRowIds).length > 1 ? 's' : ''}
          </span>
          {selectableRows.withButton ? (
            <SelectedRowsButtonAction
              buttonText={selectableRows.buttonText}
              handleClick={selectableRows.handleClick}
              selectedRows={selectedFlatRows}
            />
          ) : (
            <SelectedRowsSelectAction
              options={selectableRows.options}
              handleChange={selectableRows.handleChange}
              selectedRows={selectedFlatRows}
              updateloading={selectableRows.updateloading}
            />
          )}
        </div>
      ) : (
        ''
      )}
      <>
        {simpleBar ? (
          <SimpleBar
            onScrollCapture={handleScroll}
            ref={barRef}
            autoHide={false}
            style={{ maxHeight }}
            className={customClass}
          >
            <div style={{ position: 'relative' }} className={tableParentClasses}>
              {renderTableContent()}
            </div>
          </SimpleBar>
        ) : (
          <div
            ref={tableContainerRef}
            onScroll={handleScroll}
            className={tableParentClasses}
            style={nonSimpleBarTableStyle}
          >
            {renderTableContent()}
          </div>
        )}
        {showPagination && (!pageLoading || !loading) && (
          <Pagination
            {...{
              paginationBarClasses,
              pageSize,
              setPageSize,
              pageList,
              rows,
              pageIndex,
              setPageLoading,
              gotoPage,
              canPreviousPage,
              previousPage,
              canNextPage,
              nextPage,
              pageCount,
            }}
          />
        )}
      </>
    </>
  );
}

// @CODESMELL validator set to any
Table.propTypes = {
  columns: PropTypes.any,
  data: PropTypes.any,
  fetchData: PropTypes.any,
  loading: PropTypes.any,
  search: PropTypes.any,
  filters: PropTypes.any,
  noRecordsFound: PropTypes.any,
  defaultSortBy: PropTypes.any,
  defaultDesc: PropTypes.any,
  selectableRows: PropTypes.any,
  dataTriggers: PropTypes.any,
  hiddenColumns: PropTypes.any,
  isFirstColumnSticky: PropTypes.any,
  renderRowSubComponent: PropTypes.any,
  defaultPageSize: PropTypes.any,
  setDefaultPageSize: PropTypes.func,
  treeExpand: PropTypes.any,
  expendRowStyle: PropTypes.any,
  expendCellStyle: PropTypes.any,
  doubleClickHandler: PropTypes.func,
  mouseDownHandler: PropTypes.func,
  mouseUpHandler: PropTypes.func,
  mouseMoveHandler: PropTypes.func,
  onClickHandler: PropTypes.func,
  onCellStyle: PropTypes.func,
  index: PropTypes.number,
  setIndex: PropTypes.func,
  sort: PropTypes.any,
  setSort: PropTypes.func,
  tableParentClasses: PropTypes.string,
  tableClasses: PropTypes.string,
  paginationBarClasses: PropTypes.string,
};

export default Table;
